import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Popup, SemanticICONS } from 'semantic-ui-react';
import TableComponent from '~/components/TableComponent';
import Layout from '~/components/Layout';
import i18n from '~/components/i18n';
import styled from 'styled-components';
import ExcelExport from '~/components/ExcelExport';
import FeedPurchaseViewModal from './FeedPurchaseViewModal';
import { formatDateGMT, formatNumber, handleErrors } from '~/utils/helpers';
import { RouteTypes } from '~/routes/constants';
import { ClimaProgramService } from '~/api/services/climaProgram';
import { ClimaProgramQuery } from '~/QueryHooks/ClimaProgramQuery';
import { El } from '~/components/Layout/style';
import { useNavigate } from 'react-router';
import { ActionWrapper } from './style';
import CustomIcon from '~/components/CustomIcon';
import PreviewModal, { FeedPurchaseFileSource } from './PreviewModal';
import Filters from './Filters';
import FilterDropdown from './FilterDropdown';

const FiltersWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;

const getTableColumns = () => [
  {
    name: i18n.t('Purchase date'),
    key: 'purchaseDate',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feeding from'),
    key: 'feedingFrom',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feeding to'),
    key: 'feedingTo',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feed Products'),
    key: 'articleDisplay'
  },
  {
    name: i18n.t('Purchased feed amount'),
    key: 'purchasedFeedAmount',
    type: 'no-unit-number',
    sorted: 'asc'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Actions')
  }
];

const FeedPurchases: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate()

  const [purchaseId, setPurchaseId] = useState(null);
  const [previewFileSource, setPreviewFileSource] = useState<FeedPurchaseFileSource | null>(null);
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumns())
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [farmerSelectedId, setFarmerSelectedId] = useState('')
  const [checkedFilters, setCheckedFilters] = useState<any[]>(() => {
    const stored = localStorage.getItem('feedPurchasesFilters');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    year: currentYear,
    search: '',
    feedProducts: [],
    createdAtFrom: '',
    createdAtTo: '',
    purchaseDateFrom: '',
    purchaseDateTo: '',
    feedingFrom: '',
    feedingTo: '',
    status: ''
  });
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const actions = [
    {
      popupMessage: t('Purchase details'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'view'
    },
    {
      popupMessage: t('Download'),
      type: 'icon',
      name: 'cloud download',
      size: 'large',
      actionType: 'download'
    },
    {
      popupMessage: t('Preview'),
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'preview',
      renderActions: getActions
    }
  ];

  const {
    data: purchasesData,
    isLoading: isBusy,
    isFetching: isFetchingPurchases,
    refetch: fetchData
  } = ClimaProgramQuery.getPurchases({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    createdAtFrom: formatDateGMT(dataFiltered.createdAtFrom),
    createdAtTo: formatDateGMT(dataFiltered.createdAtTo),
    purchaseDateFrom: formatDateGMT(dataFiltered.purchaseDateFrom),
    purchaseDateTo: formatDateGMT(dataFiltered.purchaseDateTo),
    feedingFrom: formatDateGMT(dataFiltered.feedingFrom),
    feedingTo: formatDateGMT(dataFiltered.feedingTo)
  });

  const {
    data: indexData,
    isLoading: isLoadingOptions,
    refetch: fetchOptions
  } = ClimaProgramQuery.getPurchasesIndexData();

  const {
    data: registrationData,
    isLoading: isLoadingRegData
  } = ClimaProgramQuery.getClimaRegOptionsData()

  useEffect(() => {
    fetchOptions()
    setTableColumnHeaders(getTableColumns())
  }, [t])

  useEffect(() => {
    if (registrationData && !farmerSelectedId) {
      setFarmerSelectedId(registrationData?.farmers[0]?.id)
    }
  }, [registrationData, farmerSelectedId])

  useEffect(() => {
    fetchData();
  }, [dataFiltered]);

  const { filtered, rows: rawRows } = purchasesData ? purchasesData : { filtered: 0, rows: [] };

  const rows = rawRows?.map(row => ({
    ...row,
    articleDisplay: row.feedProductName || t('Customer mix'),
    cowsDisplay: `${formatNumber(row.dairyCowsNumber, 0) ?? '-'} / ${formatNumber(row?.dairyCowsNumber, 0) ?? '-'}`
  })) || [];

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return { key: year, value: year, text: year.toString() };
  });

  const articleOptions = [
    ...(indexData?.products?.map(article => ({
      key: article.id,
      value: article.id,
      text: article.name
    })) || [])
  ];

  async function handleDownload(row) {
    setLoadingAction({
      type: 'download',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      const response = await ClimaProgramService.getClimaPurchaseFile(row?.id)
      const link = document.createElement('a')
      link.href = response.data
      link.download = row?.purchaseReceiptOriginalFilename || ''
      link.click()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function getActions(row, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    const isFileTypePdf = row?.purchaseReceiptFilename?.includes('.pdf')
    switch (actionType) {
      case 'preview':
        return isFileTypePdf && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handlePreview(row)}
            size='large'
          />}
        />
      default:
        return
    }
  }

  async function handlePreview(row) {
    setLoadingAction({
      type: 'preview',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    setIsLoadingPreview(true)
    try {
      const response = await ClimaProgramService.getClimaPurchaseFile(row?.id)
      setPreviewFileSource({
        src: response.data,
        name: row?.purchaseReceiptOriginalFilename || row?.purchaseReceiptFilename || ''
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPreview(false)
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'view':
        return setPurchaseId(row?.id);
      case 'download':
        return handleDownload(row)
      default:
        return;
    }
  }

  function renderFilters() {
    return (
      <FiltersWrapper>
        <Dropdown
          selection
          selectOnBlur={false}
          value={dataFiltered.year}
          options={yearOptions}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, year: data.value as number, page: 0 })}
        />
        {/*  <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('EVS No. / Company Name')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
        <Dropdown
          selection
          selectOnBlur={false}
          search
          placeholder={t('Filter by article')}
          multiple
          value={dataFiltered.feedProducts}
          options={articleOptions}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, feedProducts: data.value as string[], page: 0 })}
        /> */}
        {checkedFilters.length > 0 && (
          <Filters
            checkedFilters={checkedFilters}
            dataFiltered={dataFiltered}
            onChangeDataFiltered={(newData) => setDataFiltered(newData)}
          />
        )}
        {/*    <Dropdown
          selection
          selectOnBlur={false}
          placeholder={t('Status')}
          clearable
          value={dataFiltered.status}
          options={indexData?.statuses?.map(status => ({ value: status.key, text: status.label }))}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, status: data.value as string, page: 0 })}
        /> */}
      </FiltersWrapper>
    );
  }

  function handleCheckFilter(checked: boolean, filter: any) {
    if (checked) {
      const newChecked = [...checkedFilters, filter];
      setCheckedFilters(newChecked);
      localStorage.setItem('feedPurchasesFilters', JSON.stringify(newChecked));
    } else {
      const newChecked = checkedFilters.filter(f => f.key !== filter.key);
      setCheckedFilters(newChecked);
      setDataFiltered({ ...dataFiltered, [filter.fromKey]: '', [filter.toKey]: '', page: 0 });
      localStorage.setItem('feedPurchasesFilters', JSON.stringify(newChecked));
    }
  }

  function renderFeedPurchaseAction() {
    return <ActionWrapper>
      <FilterDropdown
        checkedFilters={checkedFilters}
        handleCheckFilter={handleCheckFilter}
        />
      <ExcelExport
        url={'climate-program/purchases/export'}
        fileName=''
        icon={'file excel'}
        downloadWithFileName
        data={{
          year: dataFiltered?.year,
          feedProducts: dataFiltered?.feedProducts
        }}
        />
      <El.PageHeaderActionButton
        disabled={!farmerSelectedId}
        onClick={() => navigate(RouteTypes.MANAGER_CLIMATE_PROGRAM_PURCHASE_CREATE.replace(':idFarmer', farmerSelectedId))}
        >
        <Icon name='plus' />
        {t('Add purchase')}
      </El.PageHeaderActionButton>
    </ActionWrapper>
  }

  function renderRegisterAction() {
    return <El.PageHeaderActionButton
      disabled={!farmerSelectedId}
      onClick={() => navigate(`${RouteTypes.MANAGER_PROFILE_REGISTRATIONS}#${farmerSelectedId}`)}
        >
      <Icon name='clipboard list' />
      {t('Register')}
    </El.PageHeaderActionButton>
  }

  function renderActions() {
    const farmerHasRegistration = registrationData?.farmers?.find(farmer => farmer?.id === farmerSelectedId)
    return (
      <ActionWrapper>
        <Dropdown
          selection
          selectOnBlur={false}
          value={farmerSelectedId}
          options={registrationData?.farmers?.map(farmer => ({ value: farmer.id, text: farmer.companyName }))}
          onChange={(_, data) => setFarmerSelectedId(data.value as string)}
        />
        {!farmerHasRegistration?.climateProgramRegistration ? renderRegisterAction() : renderFeedPurchaseAction()}
      </ActionWrapper>
    );
  }

  function renderPurchaseViewModal() {
    return (
      <FeedPurchaseViewModal
        purchaseId={purchaseId}
        onClose={() => setPurchaseId(null)}
      />
    );
  }

  function renderTable() {
    return (
      <TableComponent
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        loadingAction={loadingAction}
        rows={rows || []}
        total={filtered || 0}
        handleChangeDataFiltered={(newDataFiltered, tableColumnHeaders) => {
          setDataFiltered({ ...dataFiltered, ...newDataFiltered })
          setTableColumnHeaders(tableColumnHeaders)
        }}
        handleClickAction={handleActionClick}
        animated
        isFetchingData={isFetchingPurchases}
        actions={actions}
      />
    );
  }

  function renderPreviewModal() {
    return <PreviewModal
      fileSource={previewFileSource}
      handleClose={() => setPreviewFileSource(null)}
      isBusy={isLoadingPreview}
    />
  }

  return (
    <Layout
      title={t('Feed Purchases')}
      segmented
      isBusy={isBusy || isLoadingOptions || isLoadingRegData}
      filters={renderFilters()}
      actions={renderActions()}
    >
      {renderPurchaseViewModal()}
      {renderTable()}
      {renderPreviewModal()}
    </Layout>
  );
};

export default FeedPurchases;
